import type { Customer, VatValidationResult } from '@/types/nest/customer'
import { axiosClient } from '@/utils/axios-client'
import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'
import { useUserStore } from '@/store/user.store'

export const useCustomerStore = defineStore('customerStore', () => {
  const userStore = useUserStore()

  /*
   * Fetch: {@link fetchCurrentCustomer}
   */
  const currentCustomer: Ref<Customer | null> = ref<Customer | null>(null)

  /*
   * Fetch: {@link fetchCustomer}
   */
  const customer: Ref<Customer | null> = ref<Customer | null>(null)

  /*
   * Fetch: {@link fetchCustomers}
   */
  const customers: Ref<Customer[]> = ref<Customer[]>([])

  /*
   * Data: {@link currentCustomer}
   */
  async function fetchCurrentCustomer() {
    const response = await axiosClient.request<Customer>({
      url: `/customers/${userStore.currentNestUser?.customerId}`,
      method: 'GET',
    })

    currentCustomer.value = response.data
  }

  async function createCustomer(data: Customer) {
    const response = await axiosClient.request<Customer>({
      url: '/customers',
      method: 'POST',
      data: data,
    })

    customer.value = response.data
  }

  /*
   * Data: {@link customers}
   */
  async function fetchCustomers() {
    if (customers.value.length > 0) return

    const response = await axiosClient.request<Customer[]>({
      url: '/customers',
      method: 'GET',
    })

    customers.value = response.data
  }

  /*
   * Data: {@link customer}
   */
  async function fetchCustomer(id: string) {
    const response = await axiosClient.request<Customer>({
      url: `/customers/${id}`,
      method: 'GET',
    })

    customer.value = response.data
  }

  async function updateCustomer(data: Partial<Customer>) {
    if (!data.iban?.trim()) {
      data.iban = null
    }

    if (!data.bic?.trim()) {
      data.bic = null
    }

    if (!data.taxNumber?.trim()) {
      data.taxNumber = null
    }

    const response = await axiosClient.request<Customer>({
      url: `/customers/${data.id}`,
      method: 'PATCH',
      data: data,
    })

    customer.value = response.data
  }

  async function checkCustomerVat(vat: string) {
    const response = await axiosClient.request<VatValidationResult>({
      url: '/customers/information',
      method: 'GET',
      params: { vat: vat },
    })

    return response.data
  }

  return {
    customers,
    customer,
    currentCustomer,
    createCustomer,
    fetchCustomers,
    fetchCustomer,
    fetchCurrentCustomer,
    updateCustomer,
    checkCustomerVat,
  }
})
